import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';

interface Mentor {
  id: string;
  displayName: string;
  university: string | null;
  major: string | null;
  classYear: string | null;
  mentorType?: string;
  occupation?: string | null;
  pricePerSession: number;
  isVerified: boolean;
  isTopRated: boolean;
}

const formatTime12 = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Monday of the week containing the given date
const mondayOf = (d: Date): Date => {
  const out = new Date(d);
  const day = out.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
};

const WEEKS_SHOWN = 4;

const IntroCallBooking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // date (YYYY-MM-DD) -> open 24h times, sorted
  const [openByDate, setOpenByDate] = useState<Map<string, string[]>>(new Map());
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [timezone, setTimezone] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState(''); // 24h "HH:MM"
  const [submitting, setSubmitting] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [booked, setBooked] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const fetchMentor = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/mentors/${id}`);
      setMentor(response.data.data || response.data);
      setError(null);
    } catch (e) {
      console.error('Error fetching mentor:', e);
      setError('This mentor profile could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, id]);

  useEffect(() => {
    fetchMentor();
    window.scrollTo(0, 0);
  }, [fetchMentor]);

  // Load open slots covering all pageable weeks (tomorrow onward)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      setSlotsLoading(true);
      const start = new Date();
      start.setDate(start.getDate() + 1);
      const end = new Date(mondayOf(new Date()));
      end.setDate(end.getDate() + WEEKS_SHOWN * 7 - 1);

      const res = await apiService.getMentorPublicAvailability(id, fmtDate(start), fmtDate(end));
      if (cancelled) return;
      if (res.success) {
        setTimezone(res.timezone || '');
        const map = new Map<string, string[]>();
        (res.availability || []).forEach((s: { date: string; time: string; isAvailable: boolean }) => {
          if (!s.isAvailable) return;
          if (!map.has(s.date)) map.set(s.date, []);
          map.get(s.date)!.push(s.time);
        });
        map.forEach((times) => times.sort((a, b) => a.localeCompare(b)));
        setOpenByDate(map);
      } else {
        setOpenByDate(new Map());
      }
      setSlotsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <p className="meta">Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !mentor) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <p className="meta">{error || 'Mentor not found.'}</p>
          <Link className="btn btn-secondary" to="/mentors" style={{ marginTop: 16 }}>
            Back to mentors
          </Link>
        </div>
      </div>
    );
  }

  const firstName = mentor.displayName.split(' ')[0];

  // Week columns (Mon–Fri) for the current page
  const baseMonday = mondayOf(new Date());
  const weekMonday = new Date(baseMonday);
  weekMonday.setDate(weekMonday.getDate() + weekOffset * 7);
  const columns = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(weekMonday);
    d.setDate(d.getDate() + i);
    return d;
  });

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const selectedLabel = (() => {
    if (!selectedDate || !selectedTime) return null;
    const d = new Date(`${selectedDate}T12:00:00`);
    return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()} · ${formatTime12(selectedTime).toLowerCase()}`;
  })();

  const goToFullBooking = () => {
    const state = {
      mentor: {
        id: mentor.id,
        displayName: mentor.displayName,
        university: mentor.university,
        major: mentor.major,
        classYear: mentor.classYear,
        mentorType: mentor.mentorType || 'student',
        occupation: mentor.occupation || null,
        pricePerSession: mentor.pricePerSession,
        isVerified: mentor.isVerified,
        isTopRated: mentor.isTopRated,
      },
      packageType: 'single' as const,
      packagePrice: mentor.pricePerSession,
      sessionsCount: 1,
    };
    if (!user) {
      sessionStorage.setItem('intendedBooking', JSON.stringify(state));
      navigate('/login');
      return;
    }
    navigate('/mentor-booking', { state });
  };

  const handleContinue = async () => {
    if (!selectedDate || !selectedTime || submitting) return;
    if (!user) {
      sessionStorage.setItem('intendedIntroCall', mentor.id);
      navigate('/login');
      return;
    }
    setSubmitting(true);
    setBookError(null);
    const scheduledAt = new Date(`${selectedDate}T${selectedTime}:00`);
    const result = await apiService.bookIntroCall(mentor.id, scheduledAt.toISOString());
    setSubmitting(false);
    if (result.success) {
      setBooked(true);
      window.scrollTo(0, 0);
    } else {
      setBookError(result.error || 'Failed to book intro call. Please try again.');
    }
  };

  if (booked) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 560 }}>
          <div className="card" style={{ padding: 36, textAlign: 'center' }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Intro call booked
            </span>
            <h1 style={{ fontSize: 26, marginBottom: 10 }}>You're on {firstName}'s calendar</h1>
            <p className="meta" style={{ marginBottom: 6 }}>
              {selectedLabel} · 15 minutes · free
            </p>
            <p className="meta" style={{ marginBottom: 24 }}>
              {firstName} confirms within a day. You'll find the session in your dashboard.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <Link className="btn btn-primary" to="/family-dashboard">
                Go to dashboard
              </Link>
              <Link className="btn btn-secondary" to={`/mentors/${mentor.id}`}>
                Back to {firstName}'s profile
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ paddingTop: 40, paddingBottom: 0 }}>
      <div className="wrap" style={{ maxWidth: 1060 }}>
        <Link className="meta" to={`/mentors/${mentor.id}`}>
          ← Back to {mentor.displayName}
        </Link>

        <div className="intro-layout">
          {/* Left panel */}
          <div>
            <h1 style={{ fontSize: 28, lineHeight: 1.25, marginTop: 16, marginBottom: 24 }}>
              Take the first step toward achieving your goals.
            </h1>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="intro-choice on">
                <input type="radio" name="calltype" checked readOnly style={{ width: 'auto', marginTop: 3 }} />
                <span>
                  <b style={{ fontWeight: 600 }}>Book a free intro call</b>
                  <br />
                  <span className="meta">Explore {firstName}'s offerings</span>
                </span>
              </label>
              <label className="intro-choice" onClick={goToFullBooking}>
                <input type="radio" name="calltype" checked={false} readOnly style={{ width: 'auto', marginTop: 3 }} />
                <span>
                  <b style={{ fontWeight: 600 }}>Book a session</b>
                  <br />
                  <span className="meta">Work with {firstName} to achieve your goals</span>
                </span>
              </label>
            </div>

            <div style={{ marginTop: 22 }}>
              <div className="meta" style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>
                Duration
              </div>
              <div className="intro-duration">
                15 minutes
                <Lock size={14} />
              </div>
            </div>
          </div>

          {/* Right panel — calendar */}
          <div className="card intro-cal">
            <div className="intro-cal-head">
              <div>
                <h3 style={{ marginBottom: 2 }}>{firstName}'s Calendar</h3>
                <p className="meta">All times shown in {timezone || 'mentor time'}</p>
              </div>
              <div className="activity-arrows">
                <button
                  className="arrow-btn"
                  aria-label="Previous week"
                  disabled={weekOffset === 0}
                  style={weekOffset === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="arrow-btn"
                  aria-label="Next week"
                  disabled={weekOffset === WEEKS_SHOWN - 1}
                  style={weekOffset === WEEKS_SHOWN - 1 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  onClick={() => setWeekOffset((w) => Math.min(WEEKS_SHOWN - 1, w + 1))}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {slotsLoading ? (
              <p className="meta">Loading available times…</p>
            ) : (
              <div className="intro-grid">
                {columns.map((day) => {
                  const dateStr = fmtDate(day);
                  const isPast = day < tomorrow;
                  const times = openByDate.get(dateStr) || [];
                  return (
                    <div key={dateStr} className="intro-day">
                      <div className="intro-day-head">
                        <div style={{ fontWeight: 600 }}>{DAY_NAMES[day.getDay()]}</div>
                        <div className="meta">
                          {day.getMonth() + 1}/{day.getDate()}
                        </div>
                      </div>
                      <div className="intro-day-slots">
                        {isPast || times.length === 0 ? (
                          <span className="meta intro-fullybooked">
                            {isPast ? 'Unavailable' : 'Fully booked'}
                          </span>
                        ) : (
                          times.map((t) => {
                            const on = selectedDate === dateStr && selectedTime === t;
                            return (
                              <button
                                key={t}
                                className={`slot${on ? ' on' : ''}`}
                                onClick={() => {
                                  setSelectedDate(dateStr);
                                  setSelectedTime(t);
                                  setBookError(null);
                                }}
                              >
                                {formatTime12(t)}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="intro-footer">
        <div className="wrap intro-footer-inner">
          <div>
            <div style={{ fontWeight: 600 }}>{selectedLabel || 'No time slot selected'}</div>
            <Link className="meta" to="/messages" style={{ textDecoration: 'underline' }}>
              Can't find a time? Message {firstName} ›
            </Link>
            {bookError && (
              <div className="meta" style={{ color: 'var(--cardinal)', marginTop: 4 }}>
                {bookError}
              </div>
            )}
          </div>
          <button
            className="btn btn-primary"
            disabled={!selectedDate || !selectedTime || submitting}
            style={
              !selectedDate || !selectedTime || submitting
                ? { opacity: 0.5, cursor: 'not-allowed' }
                : undefined
            }
            onClick={handleContinue}
          >
            {submitting ? 'Booking…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroCallBooking;
